/**
 * Chip picker for a menu item's selectable options ("which sandwich").
 * Same visual language as the salad topping chips: outlined pill that turns
 * solid HMH blue with a checkmark when selected. Single-select: exactly one
 * option per order (picking a chip replaces the previous pick).
 */

export interface ItemOptions {
  prompt: string | null;
  options: { id: number; name: string }[];
}

interface ItemOptionsPickerProps {
  itemOptions: ItemOptions;
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

export function ItemOptionsPicker({ itemOptions, selected, onChange, disabled }: ItemOptionsPickerProps) {
  const toggle = (id: number) => {
    if (disabled) return;
    onChange([id]);  // single-select: one option per order
  };

  return (
    <div className="mt-2 mb-3">
      <div className="flex items-center gap-2 mb-3">
        <h4 className="text-sm font-bold uppercase tracking-wider text-hmh-blue">
          {itemOptions.prompt || 'Choose your option(s)'}
        </h4>
        <div className="flex-1 h-0.5 bg-gray-200 rounded-full"></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {itemOptions.options.map((o) => {
          const isSelected = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => toggle(o.id)}
              disabled={disabled}
              className={`
                px-4 py-2.5 rounded-full font-medium text-sm
                transition-all duration-200 ease-in-out
                ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'}
                ${isSelected
                  ? 'bg-hmh-blue text-white shadow-md'
                  : 'bg-white border-2 border-hmh-blue text-hmh-blue hover:bg-hmh-blue/10'}
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hmh-blue
              `}
            >
              {isSelected && (
                <span className="inline-flex items-center mr-1.5">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
              )}
              <span>{o.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
